<script lang="ts" setup>
import type { TicketCard } from '~/composables/useChatTicketCard';
import { TICKET_BODY_MAX_LENGTH, TICKET_TITLE_MAX_LENGTH, ticketLink } from '~/utils/chat-ticket';

const props = defineProps<{
  card: TicketCard;
  hasShop: boolean;
}>();

const emit = defineEmits<{
  create: [];
  close: [];
}>();

const { t } = useI18n();

const NOTE_KEYS: Record<TicketCard['source'], string> = {
  suge: 'chat.ticket.hint',
  failed: 'chat.ticket.proposal_failed',
  empty: 'chat.ticket.empty_hint',
};

const editable = computed(() => props.card.status === 'editing' || props.card.status === 'error');
const canCreate = computed(
  () => editable.value && props.hasShop && props.card.title.trim() !== '' && props.card.summary.trim() !== '',
);
</script>

<template>
  <div
    v-if="card.status === 'created'"
    class="ticket-card ticket-card--created"
  >
    <div class="ticket-card__created">
      <v-icon
        color="success"
        size="18"
      >
        mdi-check-circle
      </v-icon>
      <span>{{ t('chat.ticket.created') }}</span>
      <span class="ticket-card__dot">·</span>
      <span>{{ t('chat.ticket.status_open') }}</span>
      <span class="ticket-card__dot">·</span>
      <nuxt-link :to="ticketLink(card.ticketUid ?? '')">{{ t('chat.ticket.open') }}</nuxt-link>
    </div>
    <p class="ticket-card__note">{{ t('chat.ticket.owner_note') }}</p>
  </div>

  <div
    v-else
    class="ticket-card"
  >
    <div class="ticket-card__head">
      <v-icon size="18">mdi-ticket-outline</v-icon>
      <span class="ticket-card__title">{{ t('chat.ticket.title') }}</span>
    </div>

    <div
      v-if="card.status === 'loading'"
      class="ticket-card__loading"
    >
      <v-progress-circular
        indeterminate
        size="14"
        width="2"
      />
      {{ t('chat.ticket.loading') }}
    </div>

    <template v-else>
      <p class="ticket-card__note">
        {{ t(NOTE_KEYS[card.source]) }}
      </p>
      <!-- eslint-disable vue/no-mutating-props -- the card object belongs to useChatTicketCard(). -->
      <!-- The labels stand above the fields as in m-textarea. A floating label touches the first line of the summary. -->
      <div>
        <m-input-label :text="t('chat.ticket.field_title')" />
        <v-text-field
          v-model="card.title"
          :aria-label="t('chat.ticket.field_title')"
          :maxlength="TICKET_TITLE_MAX_LENGTH"
          :disabled="!editable"
          variant="outlined"
          density="compact"
          hide-details
        />
      </div>
      <div>
        <m-input-label :text="t('chat.ticket.field_summary')" />
        <v-textarea
          v-model="card.summary"
          :aria-label="t('chat.ticket.field_summary')"
          :maxlength="TICKET_BODY_MAX_LENGTH"
          :disabled="!editable"
          rows="4"
          auto-grow
          max-rows="10"
          variant="outlined"
          density="compact"
          hide-details
        />
      </div>
      <!-- eslint-enable vue/no-mutating-props -->
      <p
        v-if="card.status === 'error'"
        class="ticket-card__error"
      >
        {{ t('chat.ticket.error') }}
      </p>
      <p
        v-if="!hasShop"
        class="ticket-card__error"
      >
        {{ t('chat.ticket.no_shop') }}
      </p>
      <div class="ticket-card__actions">
        <m-btn
          size="small"
          variant="text"
          :disabled="card.status === 'creating'"
          @click="emit('close')"
        >
          {{ t('chat.ticket.cancel') }}
        </m-btn>
        <m-btn
          size="small"
          color="primary"
          prepend-icon="mdi-send-outline"
          :loading="card.status === 'creating'"
          :disabled="!canCreate"
          @click="emit('create')"
        >
          {{ t('chat.ticket.submit') }}
        </m-btn>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.ticket-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--color-stroke-bold);
  border-radius: var(--radius-input);
  background: white;

  &--created {
    background: var(--color-canvas);
  }

  &__head,
  &__created,
  &__loading {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 13px;
  }

  &__title {
    font-weight: 600;
  }

  &__dot {
    color: var(--color-ds-tertiary);
  }

  &__note {
    margin: 0;
    font-size: 12px;
    color: var(--color-ds-secondary);
  }

  &__error {
    margin: 0;
    font-size: 12px;
    color: rgb(var(--v-theme-error));
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }
}
</style>
